import { Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { CSSProperties } from 'react';
import type { CSSObject } from 'tss-react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DottedText } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';

export interface RecommendationInfoProps {
  title: string;
  subTitle?: string;
  objectives?: string[];
  tooltip?: string;
  index?: number;
  toolTipStyles?: CSSObject;
  titleTypographyStyles?: CSSProperties;
  header?: string;
  infoSectionTitle?: string;
  infoSection?: string;
}

const RecommendationInfo: React.FC<RecommendationInfoProps> = ({
  title,
  subTitle,
  objectives,
  index,
  tooltip,
  toolTipStyles,
  header = 'Standard',
  infoSection,
  infoSectionTitle,
}) => {
  const useStyles = makeStyles()(() => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    title: {
      fontSize: '22px',
      lineHeight: '35px',
      fontWeight: 500,
      letterSpacing: '0px',
      color: patientAccessTheme.palette.text.highemphasis,
    },
    subtitle: {
      fontSize: '14px',
      lineHeight: '22px',
      fontWeight: 400,
      letterSpacing: '0px',
      color: patientAccessTheme.palette.text.highemphasis,
      opacity: '90%',
    },
    tooltip: {
      backgroundColor: patientAccessTheme.palette.grey[900],
      padding: '10px',
      fontSize: '13px',
      fontFamily: 'Inter',
      fontWeight: 400,
      lineHeight: '16px',

      marginTop: '2px',
      color: patientAccessTheme.palette.status.white,
      ...toolTipStyles,
    },
    arrow: {
      color: patientAccessTheme.palette.grey[900],
      marginLeft: '50px',
    },
    popper: {
      marginTop: '-4px',
      marginLeft: '-34px',
    },
  }));
  const { classes } = useStyles();
  return (
    <Grid className={classes.root} id={title}>
      <Typography
        className={classes.subtitle}
        style={{ marginBottom: '-0.5rem' }}
      >
        {header} {index !== undefined && index + 1}:
      </Typography>
      <Typography className={classes.title}>{title}</Typography>
      {subTitle && (
        <Typography className={classes.subtitle}>{subTitle}</Typography>
      )}
      {infoSection && (
        <Grid>
          <Typography
            fontSize="14px"
            fontType="semiBold"
            textColor={patientAccessTheme.palette.text.highemphasis}
          >
            {infoSectionTitle}
          </Typography>
          <Typography
            fontSize="14px"
            fontType="regular"
            lineHeight="26.6px"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
          >
            {infoSection}
          </Typography>
        </Grid>
      )}
      {objectives && (
        <>
          <Grid style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontSize="14px" className={classes.title}>
              Objectives
            </Typography>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip
                arrow
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                  popper: classes.popper,
                }}
                title={tooltip || ''}
                placement="bottom-start"
              >
                <InfoOutlinedIcon
                  style={{
                    height: '16px',
                    width: '16px',
                    marginLeft: '0.5rem',
                    fill: patientAccessTheme.palette.status.icon,
                  }}
                />
              </Tooltip>
              <DottedText
                typographyStyles={{
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Inter',
                }}
                text="Why this standard is important?"
              />
            </div>
          </Grid>
          <ul>
            {objectives?.map((listItem, index) => (
              <li key={index}>
                <Typography lineHeight="27px" className={classes.subtitle}>
                  {listItem}
                </Typography>
              </li>
            ))}
          </ul>
        </>
      )}
    </Grid>
  );
};

export default RecommendationInfo;
